import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import Container from '../../components/shared/Container';
import MenuCategoriesSlider from '../../components/shared/MenuCategoriesSlider';
import { useMenuDrawer } from '../../components/shared/MenuDrawerProvider';
import { useHeader } from '../../context/HeaderContext';
import { getMenuItemsByCategorySlug, getMenuCategories, getMenuItemBySlug, getRelatedMenuItems } from '../../lib/sanity';
import SEO from '../../components/shared/SEO';
import StructuredData from '../../components/shared/StructuredData';
import { generateBreadcrumbSchema } from '../../utils/schema';
import { getSanityImageProps } from '../../utils/sanityImage';

// Helper function to extract plain text from portable text
const extractPlainText = (portableText) => {
  if (!portableText) return '';
  if (typeof portableText === 'string') return portableText;
  if (Array.isArray(portableText)) {
    return portableText
      .map(block => {
        if (block._type === 'block' && block.children) {
          return block.children.map(child => child.text).join('');
        }
        return '';
      })
      .join(' ');
  }
  return '';
};

export default function CategoryPage() {
  const { category: categorySlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUseBlurredBackground } = useHeader();
  const [categoryData, setCategoryData] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use the drawer context instead of local state
  const { 
    openDrawer, 
    closeDrawer, 
    startLoading, 
    setError: setDrawerError, 
    finishLoading,
    getItemFromCache,
    getRelatedFromCache
  } = useMenuDrawer();

  // Function to handle opening the drawer with a menu item
  const openMenuItemDrawer = useCallback(async (itemSlug) => {
    try {
      // Start loading state
      startLoading();
      
      // Open the drawer immediately with loading state
      openDrawer(null, categorySlug, [], itemSlug);
      
      // Check if we already have this item in cache
      const cachedItem = getItemFromCache(itemSlug);
      
      if (cachedItem) {
        // Use cached item data
        const cachedRelated = getRelatedFromCache(cachedItem._id) || [];
        openDrawer(cachedItem, categorySlug, cachedRelated, itemSlug);
        finishLoading();
        return;
      }
      
      // Fetch the menu item data
      const item = await getMenuItemBySlug(itemSlug);
      
      if (!item) {
        setDrawerError('Menu item not found');
        return;
      }
      
      // Fetch related items
      let related = [];
      if (item.category && item.category._id) {
        related = await getRelatedMenuItems(item._id, item.category._id, 2) || [];
      }
      
      // Update the drawer with the fetched data
      openDrawer(item, categorySlug, related, itemSlug);
    } catch (err) {
      console.error(`Error fetching menu item ${itemSlug}:`, err);
      setDrawerError('Failed to load menu item. Please try again later.');
    } finally {
      finishLoading();
    }
  }, [categorySlug, startLoading, setDrawerError, openDrawer, finishLoading, getItemFromCache, getRelatedFromCache]);
  
  // Function to close the drawer
  const closeMenuItemDrawer = useCallback(() => {
    closeDrawer();
    
    // Also update URL directly in case the provider's method fails
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('item')) {
      searchParams.delete('item');
      const newUrl = `${location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
      navigate(newUrl, { replace: true });
    }
  }, [closeDrawer, location, navigate]);
  
  // Check URL for menu item on initial load or URL change
  useEffect(() => {
    // Get search parameters
    const searchParams = new URLSearchParams(location.search);
    const itemSlug = searchParams.get('item');
    
    // If we have an item parameter, open the drawer
    if (itemSlug) {
      // Only fetch if we haven't already fetched this data
      if (!dataFetchedRef.current) {
        dataFetchedRef.current = true;
        openMenuItemDrawer(itemSlug);
      }
    } else {
      // Reset the data fetched flag when there's no item in the URL
      dataFetchedRef.current = false;
    }
  }, [location.search, openMenuItemDrawer]);

  // Use a ref to track if data has been loaded to prevent multiple fetches
  const dataFetchedRef = useRef(false);
  
  // Enable blurred background for header on category pages
  useEffect(() => {
    setUseBlurredBackground(true);
    
    // Clean up when component unmounts
    return () => {
      setUseBlurredBackground(false);
    };
  }, [setUseBlurredBackground]);
  
  useEffect(() => {
    // Skip if data has already been fetched for this category
    if (dataFetchedRef.current && categoryData && categoryData.slug === categorySlug) return;
    
    async function fetchCategoryData() {
      try {
        setLoading(true);
        const result = await getMenuItemsByCategorySlug(categorySlug);
        
        if (!result.category) {
          setError('Category not found');
          return;
        }
        
        setCategoryData(result.category);
        setMenuItems(result.menuItems || []);
        setError(null);
        
        // Mark data as fetched for this category
        dataFetchedRef.current = true;
      } catch (err) {
        console.error(`Error fetching category ${categorySlug}:`, err);
        setError('Failed to load category. Please try again later.');
      } finally {
        setLoading(false);
      }
    }

    if (categorySlug) {
      fetchCategoryData();
    }
  }, [categorySlug, categoryData]);

  // Fetch all menu categories for the slider
  const categoriesFetchedRef = useRef(false);
  
  useEffect(() => {
    // Skip if categories have already been fetched
    if (categoriesFetchedRef.current && allCategories.length > 0) return;
    
    async function fetchAllCategories() {
      try {
        setCategoriesLoading(true);
        const categories = await getMenuCategories();
        
        if (categories && categories.length > 0) {
          // Filter out the current category
          const filteredCategories = categories.filter(cat => cat.slug !== categorySlug);
          setAllCategories(filteredCategories);
          
          // Mark categories as fetched
          categoriesFetchedRef.current = true;
        }
      } catch (err) {
        console.error('Error fetching all menu categories:', err);
      } finally {
        setCategoriesLoading(false);
      }
    }

    fetchAllCategories();
  }, [categorySlug, allCategories.length]);

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  if (loading) {
    return (
      <div className="bg-brand-cream min-h-screen">
        {/* Hero Section Skeleton */}
        <div className="relative h-[400px] bg-gray-200 animate-pulse">
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-300 to-transparent"></div>
          
          <Container className="relative h-full">
            <div className="absolute bottom-8 left-0 right-0 w-full px-6 md:px-8 lg:px-12">
              <div className="flex flex-col md:flex-row md:justify-between md:items-end">
                {/* Left side - Title and Back link */}
                <div className="mb-4 md:mb-0">
                  <div className="mb-4">
                    <div className="flex items-center text-sm">
                      <div className="h-4 w-12 bg-gray-300 rounded"></div>
                      <div className="mx-2 h-3 w-3 bg-gray-300 rounded"></div>
                      <div className="h-4 w-12 bg-gray-300 rounded"></div>
                      <div className="mx-2 h-3 w-3 bg-gray-300 rounded"></div>
                      <div className="h-4 w-24 bg-gray-300 rounded"></div>
                    </div>
                  </div>
                  <div className="h-10 w-48 bg-gray-300 rounded"></div>
                </div>
                
                {/* Right side - Description */}
                <div className="md:max-w-xl">
                  <div className="h-4 bg-gray-300 rounded w-full"></div>
                  <div className="h-4 bg-gray-300 rounded w-5/6 mt-1"></div>
                </div>
              </div>
            </div>
          </Container>
        </div>
        
        {/* Categories Slider Skeleton */}
        <div className="bg-white py-6 border-b border-gray-200">
          <Container>
            <div className="flex space-x-6 overflow-x-auto pb-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex-shrink-0 animate-pulse">
                  <div className="h-8 w-32 bg-gray-200 rounded-full"></div>
                </div>
              ))}
            </div>
          </Container>
        </div>
        
        {/* Menu Items Skeleton */}
        <div className="bg-white py-12">
          <Container>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="border border-gray-200 rounded-lg overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-4">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6 mt-1"></div>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </div>
      </div>
    );
  }

  if (error || !categoryData) {
    return (
      <div className="bg-brand-cream min-h-screen">
        <SEO 
          title="Category Not Found | Original Pancake House"
          description="We couldn't find the category you're looking for."
        />
        <Container>
          <div className="py-32 text-center">
            <h1 className="text-3xl font-heading text-brand-navy mb-4">Category Not Found</h1>
            <p className="text-gray-700 mb-6">{error || "We couldn't find the category you're looking for."}</p>
            <Link to="/menu" className="text-brand-navy underline">Return to Menu</Link>
          </div>
        </Container>
      </div>
    );
  }
  
  // Get search parameters for SEO
  const searchParams = new URLSearchParams(location.search);
  const itemSlug = searchParams.get('item');
  const { selectedItem } = useMenuDrawer();
  
  // Determine page title and description based on selected item or category
  const pageTitle = selectedItem 
    ? `${selectedItem.name} | ${categoryData.name} | Original Pancake House` 
    : `${categoryData.name} | Menu | The Original Pancake House`;
  
  const pageDescription = selectedItem
    ? extractPlainText(selectedItem.description) || `Discover our delicious ${selectedItem.name} from our ${categoryData.name} menu. Made fresh with quality ingredients at The Original Pancake House.`
    : extractPlainText(categoryData.description) || `Explore our ${categoryData.name} menu featuring made-from-scratch breakfast and brunch favorites. Handcrafted with fresh, quality ingredients at The Original Pancake House.`;

  return (
    <div className="bg-brand-cream min-h-screen">
      <SEO 
        title={pageTitle}
        description={pageDescription}
        openGraph={selectedItem ? {
          type: 'article',
          image: selectedItem.imageUrl || categoryData.imageUrl || "/oph-social-share2.jpg"
        } : {
          image: categoryData.imageUrl || "/oph-social-share2.jpg"
        }}
      />
      
      {/* Structured Data for SEO */}
      <StructuredData data={generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Menu', url: '/menu' },
        { name: categoryData.name, url: `/menu/${categorySlug}` }
      ])} />
      
      {/* Hero Section */}
      <motion.div 
        className="relative h-[400px] bg-brand-navy"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          backgroundImage: categoryData.imageUrl ? `url(${categoryData.imageUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Gradient Overlay - Darker on mobile for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-brand-navy/30 lg:via-transparent lg:to-transparent"></div>
        
        <Container className="relative h-full">
          <div className="absolute bottom-8 left-0 right-0 w-full px-6 md:px-8 lg:px-12">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end">
              {/* Left side - Title and Back link */}
              <div className="mb-4 md:mb-0">
                <div className="mb-4">
                  <div className="flex items-center text-sm font-medium">
                    <Link 
                      to="/"
                      className="text-white hover:text-brand-cream transition-colors"
                    >
                      Home
                    </Link>
                    
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mx-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    
                    <Link 
                      to="/menu"
                      className="text-white hover:text-brand-cream transition-colors"
                    >
                      Menu
                    </Link>
                    
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mx-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    
                    <span className="text-white text-opacity-90">
                      {categoryData.name}
                    </span>
                  </div>
                </div>
                <h1 className="text-5xl md:text-6xl font-heading text-white drop-shadow-lg">{categoryData.name}</h1>
              </div>
              
              {/* Right side - Description */}
              {categoryData.description && (
                <div className="md:max-w-xl">
                  <div className="text-white text-opacity-90 drop-shadow-md prose prose-invert max-w-none">
                    <PortableText value={categoryData.description} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </Container>
      </motion.div>

      {/* Menu Items */}
      <div className="bg-white py-16">
        <Container>
          {menuItems.length > 0 ? (
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              {menuItems.map((item) => (
                <motion.div 
                  key={item._id}
                  className="block"
                  variants={itemVariant}
                >
                  <div className="border border-brand-navy rounded-lg overflow-hidden h-full">
                    <div 
                      onClick={(e) => {
                        e.preventDefault();
                        openMenuItemDrawer(item.slug);
                      }}
                      className="block h-full cursor-pointer"
                    >
                      <div className="flex flex-col h-full">
                        {/* Item Image - Takes up full width */}
                        <div className="w-full h-48 overflow-hidden">
                          {item.imageUrl ? (
                            <img 
                              {...getSanityImageProps(item.imageUrl, 'menuCard')}
                              alt={item.name} 
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        
                        {/* Item Details */}
                        <div className="flex-1 p-4">
                          <div className="flex justify-between items-start">
                            <h3 className="font-bold text-brand-navy text-lg">{item.name}</h3>
                            {item.calories && (
                              <div className="text-xs text-gray-500 ml-2">{item.calories} cal</div>
                            )}
                          </div>
                          {item.plainDescription && (
                            <p className="text-sm text-gray-600 mt-2 line-clamp-3">{item.plainDescription}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">No menu items found in this category.</p>
            </div>
          )}
        </Container>
      </div>

      {/* Other Menu Categories Slider */}
      {allCategories.length > 0 && (
        <MenuCategoriesSlider 
          categories={allCategories}
          loading={categoriesLoading}
          title="EXPLORE MORE CATEGORIES"
          subtitle="Discover more delicious options from our menu"
        />
      )}
    </div>
  );
}
